/**
 * Helpers for the guided apply journey (anonymous → signup → profile → apply).
 *
 * The `next` query parameter is the canonical URL-based persistence layer
 * for the journey: every step preserves it so the user can refresh, switch
 * tabs, or come back via an email link without losing context. These helpers
 * parse and build that parameter consistently across CTA buttons, auth pages,
 * the auth callback, and the profile-setup wizard.
 */

import { isSafeRedirectPath } from '@kit/shared/utils';

export interface ApplyJourneyTarget {
  /** 'audition' for /auditions/[slug], 'competition' for /competitions/[slug] */
  surface: 'audition' | 'competition';
  /** Audition or competition slug */
  slug: string;
  /** Pre-selected style entry ids (competitions only, multi-select) */
  styleEntryIds?: string[];
  /**
   * Shared competition access code carried from an emailed invite link
   * (`/competitions/<slug>?code=...`). Preserved across the signup journey
   * so a logged-out finalist auto-redeems it post-auth. Competitions only.
   */
  code?: string;
}

/**
 * Parse the `next` query parameter into a structured journey target.
 *
 * Returns `null` when the URL doesn't look like an audition or competition
 * apply URL — callers should fall back to non-journey behavior in that case.
 *
 * Accepted shapes:
 *   /auditions/{slug}?apply=true
 *   /competitions/{slug}?apply=true
 *   /competitions/{slug}?apply=true&styles={uuid},{uuid}
 */
export function parseApplyJourneyNext(
  next: string | null | undefined,
): ApplyJourneyTarget | null {
  if (!next || !isSafeRedirectPath(next)) return null;

  let url: URL;
  try {
    url = new URL(next, 'http://placeholder.local');
  } catch {
    return null;
  }

  const pathname = url.pathname;
  const apply = url.searchParams.get('apply') === 'true';
  if (!apply) return null;

  const auditionMatch = pathname.match(/^\/auditions\/([^/?#]+)$/);
  if (auditionMatch && auditionMatch[1]) {
    return { surface: 'audition', slug: auditionMatch[1] };
  }

  const competitionMatch = pathname.match(/^\/competitions\/([^/?#]+)$/);
  if (competitionMatch && competitionMatch[1]) {
    const stylesParam = url.searchParams.get('styles');
    const styleEntryIds = stylesParam
      ? stylesParam.split(',').filter(Boolean)
      : undefined;
    const code = url.searchParams.get('code') ?? undefined;
    return {
      surface: 'competition',
      slug: competitionMatch[1],
      ...(styleEntryIds && styleEntryIds.length > 0 ? { styleEntryIds } : {}),
      ...(code ? { code } : {}),
    };
  }

  return null;
}

/**
 * Resolve the apply-journey target for a post-signup consumer (the dancer
 * profile-setup wizard), preferring the URL `next` and falling back to the
 * localStorage intent.
 *
 * The URL `next` is the primary, most-specific signal. But it can be dropped
 * between the entry CTA and the wizard — notably when a brand-new dancer
 * signup is bounced through the `/welcome` role picker, whose redirect strips
 * the query string. The localStorage intent (written by the entry CTA) is the
 * recovery net for exactly that case, so the journey survives any drop of the
 * URL param. Returns null when neither source yields a target.
 */
export function resolveApplyJourneyTarget(
  next: string | null | undefined,
  storedIntent: Pick<
    PendingAuditionIntent,
    'surface' | 'slug' | 'styleEntryIds' | 'code'
  > | null,
): ApplyJourneyTarget | null {
  const fromUrl = parseApplyJourneyNext(next);
  if (fromUrl) return fromUrl;

  if (!storedIntent?.slug || !storedIntent?.surface) return null;
  return {
    surface: storedIntent.surface,
    slug: storedIntent.slug,
    ...(storedIntent.styleEntryIds && storedIntent.styleEntryIds.length > 0
      ? { styleEntryIds: storedIntent.styleEntryIds }
      : {}),
    ...(storedIntent.code ? { code: storedIntent.code } : {}),
  };
}

/**
 * Build the `next` query value for an apply journey target. Returns the
 * raw path; callers should `encodeURIComponent` it when adding to a query
 * string.
 */
export function buildApplyJourneyNext(target: ApplyJourneyTarget): string {
  const base =
    target.surface === 'audition'
      ? `/auditions/${target.slug}`
      : `/competitions/${target.slug}`;
  const params = new URLSearchParams();
  params.set('apply', 'true');
  if (target.styleEntryIds && target.styleEntryIds.length > 0) {
    params.set('styles', target.styleEntryIds.join(','));
  }
  if (target.code) params.set('code', target.code);
  return `${base}?${params.toString()}`;
}

export type ApplyAuthMode = 'signin' | 'signup';

/**
 * Build the sign-in or sign-up URL for an apply journey. Sign-up additionally
 * carries `type=dancer` so the auth UI knows to render the dancer flow.
 */
export function buildAuthUrlForApply(
  target: ApplyJourneyTarget,
  mode: ApplyAuthMode,
): string {
  const next = buildApplyJourneyNext(target);
  const params = new URLSearchParams();
  params.set('next', next);
  if (mode === 'signup') params.set('type', 'dancer');
  const path = mode === 'signup' ? '/auth/sign-up' : '/auth/sign-in';
  return `${path}?${params.toString()}`;
}

/**
 * Append `?next=<encoded>` to a path when `next` is non-empty, otherwise
 * return the path unchanged. Used to preserve the apply journey across
 * sign-in ↔ sign-up navigation.
 */
export function withNextParam(
  path: string,
  next: string | null | undefined,
): string {
  if (!next) return path;
  return `${path}?next=${encodeURIComponent(next)}`;
}

/**
 * Localstorage key used to persist the user's apply intent across browser
 * sessions. Cleared once the application is submitted (or explicitly
 * dismissed via the resume banner).
 */
export const PENDING_AUDITION_INTENT_KEY = 'ballee.pending_audition_intent';

export interface PendingAuditionIntent {
  surface: 'audition' | 'competition';
  slug: string;
  title: string;
  styleEntryIds?: string[];
  /** Shared competition access code from an emailed invite link. */
  code?: string;
  /** ISO timestamp — used to expire stale intents client-side */
  storedAt: string;
}

/**
 * Browser-only helpers for the localStorage backup layer.
 *
 * These are no-ops on the server (typeof window check). Callers don't
 * need to guard.
 */
export const pendingAuditionIntent = {
  read(): PendingAuditionIntent | null {
    if (typeof window === 'undefined') return null;
    try {
      const raw = window.localStorage.getItem(PENDING_AUDITION_INTENT_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as PendingAuditionIntent;
      if (!parsed?.slug || !parsed?.title || !parsed?.surface) return null;
      return parsed;
    } catch {
      return null;
    }
  },

  write(value: Omit<PendingAuditionIntent, 'storedAt'>): void {
    if (typeof window === 'undefined') return;
    try {
      const payload: PendingAuditionIntent = {
        ...value,
        storedAt: new Date().toISOString(),
      };
      window.localStorage.setItem(
        PENDING_AUDITION_INTENT_KEY,
        JSON.stringify(payload),
      );
    } catch {
      // Quota exceeded / private mode — ignore silently. The DB-backed
      // resume banner still works once the user has a draft application.
    }
  },

  clear(): void {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.removeItem(PENDING_AUDITION_INTENT_KEY);
    } catch {
      // ignore
    }
  },
};
